import React, { Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useAuthStore } from "../stores/authStore";

// Lazy load components
const LandingPage = React.lazy(() => import("../features/landing/LandingPage"));
const DemoPage = React.lazy(() => import("../features/landing/DemoPage"));
const PricingPage = React.lazy(() => import("../features/landing/PricingPage"));
const OAuthCallbackPage = React.lazy(() => import("../features/auth/OAuthCallbackPage"));
const MagicLinkVerifyPage = React.lazy(() => import("../features/auth/MagicLinkVerifyPage"));
const SsoCompletePage = React.lazy(() => import("../features/auth/SsoCompletePage"));
const VerifyEmailPage = React.lazy(() => import("../features/auth/VerifyEmailPage"));
const CheckEmailPage = React.lazy(() => import("../features/auth/CheckEmailPage"));
const ForgotPasswordPage = React.lazy(() => import("../features/auth/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("../features/auth/ResetPasswordPage"));
const BoardExplorerPage = React.lazy(() => import("../features/boards/BoardExplorerPage.tsx"));
const BoardPage = React.lazy(() => import("../features/boards/BoardPage.tsx"));
const AcceptSharePage = React.lazy(() => import("../features/boards/components/AcceptSharePage").then(module => ({ default: module.AcceptSharePage })));
const CheckoutSuccessPage = React.lazy(() => import("../features/checkout/CheckoutSuccessPage"));
const PlanPage = React.lazy(() => import("../features/subscription/PlanPage"));
const HelpPage = React.lazy(() => import("../features/help/HelpPage"));
const DocsPage = React.lazy(() => import("../features/help/DocsPage"));
const ConnectWooCommercePage = React.lazy(() => import("../features/integrations/ConnectWooCommercePage"));
const TermsOfServicePage = React.lazy(() => import("../features/legal/TermsOfServicePage"));
const PrivacyPolicyPage = React.lazy(() => import("../features/legal/PrivacyPolicyPage"));

import { PageTransition } from "../components/PageTransition";

const LoadingFallback = () => (
  <div className="min-h-screen bg-background-page flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-accent-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);


// Protected route component
const BillingBanner = React.lazy(() => import("../features/billing/BillingBanner"));
const SuspendedInterstitial = React.lazy(() => import("../features/billing/SuspendedInterstitial"));

const ProtectedRoute = ({ children, requireEmailVerification = true }: { children: React.ReactNode; requireEmailVerification?: boolean }) => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // If email verification is required and user hasn't verified, redirect to check-email
  const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
  if (requireEmailVerification && user && !user.emailVerified && !skipVerification && location.pathname !== '/check-email') {
    return <Navigate to="/check-email" replace />;
  }

  return (
    <Suspense fallback={null}>
      <SuspendedInterstitial>
        <BillingBanner />
        {children}
      </SuspendedInterstitial>
    </Suspense>
  );
};

// Public route component (redirect to appropriate page if already authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // If authenticated but not verified, redirect to check-email
  const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
  if (user && !user.emailVerified && !skipVerification) {
    return <Navigate to="/check-email" replace />;
  }

  // Verified users go to boards
  return <Navigate to="/boards" replace />;
};

const Router = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<LoadingFallback />}>
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <PageTransition>
                  <LandingPage />
                </PageTransition>
              </PublicRoute>
            }
          />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Navigate to="/?modal=login" replace />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Navigate to="/?modal=register" replace />
              </PublicRoute>
            }
          />
          <Route
            path="/demo"
            element={
              <PageTransition>
                <DemoPage />
              </PageTransition>
            }
          />
          <Route
            path="/pricing"
            element={
              <PageTransition>
                <PricingPage />
              </PageTransition>
            }
          />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />
          <Route
            path="/auth/magic-link"
            element={
              <PageTransition>
                <MagicLinkVerifyPage />
              </PageTransition>
            }
          />
          <Route
            path="/auth/sso-complete"
            element={
              <PageTransition>
                <SsoCompletePage />
              </PageTransition>
            }
          />

          {/* Email verification routes */}
          <Route
            path="/verify-email"
            element={
              <PageTransition>
                <VerifyEmailPage />
              </PageTransition>
            }
          />
          <Route
            path="/check-email"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <CheckEmailPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Password reset routes */}
          <Route
            path="/forgot-password"
            element={
              <PageTransition>
                <ForgotPasswordPage />
              </PageTransition>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PageTransition>
                <ResetPasswordPage />
              </PageTransition>
            }
          />

          {/* Protected routes */}
          <Route
            path="/boards"
            element={
              <ProtectedRoute>

                <PageTransition>
                  <BoardExplorerPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/boards/:id"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <BoardPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          <Route
            path="/plan"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <PlanPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <HelpPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/docs"
            element={
              <PageTransition>
                <DocsPage />
              </PageTransition>
            }
          />
          <Route
            path="/boards/:id/journals/:journalId"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <BoardPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />



          {/* Share routes */}
          <Route
            path="/share/journal/:token"
            element={
              <PageTransition>
                <AcceptSharePage />
              </PageTransition>
            }
          />

          {/* Checkout routes */}
          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <CheckoutSuccessPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Integration routes */}
          <Route
            path="/connect/woocommerce/start"
            element={
              <PageTransition>
                <ConnectWooCommercePage />
              </PageTransition>
            }
          />
          <Route
            path="/terms"
            element={
              <PageTransition>
                <TermsOfServicePage />
              </PageTransition>
            }
          />
          <Route
            path="/privacy"
            element={
              <PageTransition>
                <PrivacyPolicyPage />
              </PageTransition>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

export default Router;
